/**
 * Painting the graph onto a 2D context.
 *
 * Split from kb_graph_canvas.ts along a real seam: this is a pure function of
 * (scene, positions, view state) → canvas operations, with no listeners, no
 * mutable state of its own and nothing to tear down. Everything about pointers,
 * dragging and lifetimes lives on the other side of that line.
 */

import { kbTypeColor } from './kb_types.js'
import type { LayoutNode } from './kb_graph_layout.js'

export const NODE_RADIUS = 4

export type GraphScene = {
  readonly nodes: readonly { id: string; type: string; title: string }[]
  readonly edges: readonly { from: string; to: string; kind: 'relation' | 'tag' }[]
}

/** Camera plus what the pointer and the selection are currently doing. */
export type ViewState = {
  readonly scale: number
  readonly panX: number
  readonly panY: number
  readonly hover: string | null
  readonly selected: string | null
}

/** Resolve an accent token to a concrete colour, since canvas cannot use var(). */
export function tokenColour(token: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(`--${token}`).trim()
  return v || '#808098'
}

/** type → concrete colour, resolved once per mount rather than per frame. */
export function typeColours(scene: GraphScene): Map<string, string> {
  const out = new Map<string, string>()
  for (const n of scene.nodes) {
    if (!out.has(n.type)) out.set(n.type, tokenColour(kbTypeColor(n.type)))
  }
  return out
}

/** id → the ids it shares an edge with. Built once; selection reads it per frame. */
export function adjacency(scene: GraphScene): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>()
  for (const e of scene.edges) {
    if (!out.has(e.from)) out.set(e.from, new Set())
    if (!out.has(e.to)) out.set(e.to, new Set())
    out.get(e.from)?.add(e.to)
    out.get(e.to)?.add(e.from)
  }
  return out
}

export function paintGraph(
  canvas: HTMLCanvasElement,
  scene: GraphScene,
  pos: ReadonlyMap<string, LayoutNode>,
  view: ViewState,
  colours: ReadonlyMap<string, string>,
  neighbours: ReadonlyMap<string, Set<string>>,
): void {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const dpr = window.devicePixelRatio || 1
  const w = canvas.clientWidth
  const h = canvas.clientHeight
  canvas.width = Math.floor(w * dpr)
  canvas.height = Math.floor(h * dpr)
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, w, h)

  const { scale, panX, panY, hover, selected } = view
  const tx = (x: number): number => x * scale + panX
  const ty = (y: number): number => y * scale + panY

  // With a selection, everything outside its immediate neighbourhood FADES
  // rather than disappearing. Hiding it would throw away the context that makes
  // the answer meaningful — how big the rest of the graph is, and where in it
  // this node sits.
  const near = selected === null ? null : (neighbours.get(selected) ?? new Set<string>())
  const inFocus = (id: string): boolean => near === null || id === selected || near.has(id)

  // Edges first, so nodes sit on top of them.
  for (const e of scene.edges) {
    const a = pos.get(e.from)
    const z = pos.get(e.to)
    if (!a || !z) continue
    // Lit only when the edge is one of the SELECTED node's own. Lighting every
    // edge whose ends are both in focus would also light neighbour-to-neighbour
    // edges, overstating what the click asked about.
    const touches = selected !== null && (e.from === selected || e.to === selected)
    if (selected !== null && !touches) {
      ctx.strokeStyle = 'rgba(255,255,255,0.04)'
    } else if (e.kind === 'relation') {
      // A hand-drawn relation should be visible at a glance.
      ctx.strokeStyle = touches ? 'rgba(196,181,253,0.95)' : 'rgba(167,139,250,0.7)'
    } else {
      ctx.strokeStyle = touches ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.22)'
    }
    ctx.lineWidth = touches ? 1.7 : 1
    ctx.beginPath()
    ctx.moveTo(tx(a.x), ty(a.y))
    ctx.lineTo(tx(z.x), ty(z.y))
    ctx.stroke()
  }
  ctx.lineWidth = 1

  for (const n of scene.nodes) {
    const p = pos.get(n.id)
    if (!p) continue
    const isHover = n.id === hover
    const isSelected = n.id === selected
    const lit = isSelected || isHover
    // Halo nodes are drawn smaller and dimmer: still findable, still carrying
    // their type colour, but not reading with the weight of a node whose
    // position actually means something.
    let r = p.isolated ? NODE_RADIUS - 1.5 : NODE_RADIUS
    if (isSelected) r = NODE_RADIUS + 2
    if (isHover) r += 2
    ctx.globalAlpha = inFocus(n.id) ? (p.isolated && !lit ? 0.5 : 1) : 0.1
    ctx.beginPath()
    ctx.arc(tx(p.x), ty(p.y), r, 0, Math.PI * 2)
    ctx.fillStyle = colours.get(n.type) ?? '#808098'
    ctx.fill()
    if (lit) {
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = isSelected ? 2 : 1.5
      ctx.stroke()
      ctx.lineWidth = 1
    }
  }
  ctx.globalAlpha = 1
}
